import React from "react";
import {
  GraduationCapIcon,
  MapPinIcon,
  SchoolIcon,
  Contact,
  MessageCircle,
} from "lucide-react";
import { Badge, Button, Separator } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { cn } from "@/lib/utils";
import { Student } from "@/types/Student";
import { InterestPill } from "@/components/InterestPill";
import { serializeQualifiedChatId } from "@zeemee/react-web-chat";
import { StudentProfileImage } from "../StudentGrid/StudentProfileImage";
import { cleanUtf8 } from "@/util/stringUtils";
import { StudentHeader } from "../StudentGrid/StudentHeader";

export const StudentPopoverCard = ({ student }: { student: Student }) => {
  const { uuid, majors, interests, studentOrganizationStatus } = student;
  const { t } = useTranslation();
  const { navToRoute } = useProtectedNavigation();

  const { bio, location, highSchool } = studentOrganizationStatus || {};

  return (
    <div className="flex flex-col" data-testid="student-popover-card">
      <div className="overflow-hidden" data-testid="student-image-container">
        <StudentProfileImage
          student={student}
          className="h-80 rounded-t-sm rounded-b-none"
          activeClassName="absolute -right-1 bottom-0 translate-x-0 translate-y-1"
        />
      </div>

      <div
        className="flex flex-col gap-3 px-4 pt-2 pb-4"
        data-testid="student-details-container"
      >
        <div className="flex flex-col gap-2">
          <StudentHeader student={student} />
          <div
            className="flex flex-col gap-2"
            data-testid="student-info-container"
          >
            <StudentDetailsParagraph
              icon={MapPinIcon}
              text={location || t("studentCard.noLocation")}
              dataTestId="student-location"
            />
            <StudentDetailsParagraph
              icon={SchoolIcon}
              text={highSchool ? highSchool : t("studentCard.noHighSchool")}
              dataTestId="student-school"
            />
            <StudentDetailsParagraph
              icon={GraduationCapIcon}
              text={
                majors?.length
                  ? majors.map((major) => major.name).join(", ")
                  : t("studentCard.noMajors")
              }
              dataTestId="student-majors"
            />
          </div>
        </div>

        <p
          className="line-clamp-2 overflow-hidden font-sans text-sm/normal wrap-break-word text-ellipsis"
          data-testid="student-bio"
        >
          {cleanUtf8(bio) || t("studentCardPopover.noBio")}
        </p>

        {interests && interests.length > 0 ? (
          <div
            className="flex flex-wrap gap-x-1 gap-y-2"
            data-testid="student-interests-container"
          >
            {interests.slice(0, 4).map((interest, index) => {
              return (
                <InterestPill
                  key={interest.id}
                  label={interest.name ?? ""}
                  data-testid={`student-interest-badge-${index}`}
                />
              );
            })}
            {interests.length > 4 && (
              <Badge
                variant="outline"
                className="rounded-full px-[11px] py-[3px] text-xs font-medium text-foreground"
                data-testid="student-interest-badge-more"
              >
                +{interests.length - 4}
              </Badge>
            )}
          </div>
        ) : (
          <Badge
            variant="dashed"
            data-testid="student-no-interests-badge"
            className="rounded-full px-[11px] py-[3px] text-xs text-muted-foreground"
          >
            {t("studentCard.noInterests")}
          </Badge>
        )}
      </div>

      <div
        className="flex flex-col gap-6 pb-4"
        data-testid="student-actions-container"
      >
        <Separator data-testid="student-actions-separator" />
        <div className="flex items-center gap-2 px-4">
          <Button
            size="lg"
            variant="outline"
            className="w-[140px] gap-2 py-2 text-center"
            data-testid="student-profile-button"
            disabled={!student.id}
            onClick={() => {
              if (student.id)
                navToRoute(
                  "student",
                  { studentId: student.id },
                  {},
                  { newTab: true },
                );
            }}
          >
            <Contact className="h-4 w-4" />
            {t("studentCardPopover.profile")}
          </Button>
          <Button
            size="lg"
            variant="default"
            className="w-[140px] gap-2 py-2 text-center"
            onClick={() =>
              navToRoute("selectedChat", {
                chat:
                  serializeQualifiedChatId({
                    chatType: "chat",
                    chatId: uuid || "",
                  }) || "",
              })
            }
            data-testid="student-message-button"
          >
            <MessageCircle className="h-4 w-4" />
            {t("studentCardPopover.message")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const StudentDetailsParagraph = ({
  icon: Icon,
  text,
  dataTestId,
  className,
}: {
  icon: React.ElementType;
  text: string;
  dataTestId?: string;
  className?: string;
}) => (
  <p
    className={cn(
      "text-md/4 flex flex-row items-center gap-1 text-muted-foreground",
      className,
    )}
    data-testid={dataTestId}
  >
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span className="block min-w-0 truncate">{text}</span>
  </p>
);
